import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LoginPage = () => {
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const sendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email");
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email: email.trim().toLowerCase() });
      toast.success("Code sent — check your inbox");
      setStep("code");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-code", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      localStorage.setItem("mm_token", res.data.token);
      setUser(res.data.user);
      const u = res.data.user;
      toast.success("Welcome");
      if (u.is_admin) nav("/admin");
      else if (!u.profile_complete) nav("/onboarding");
      else nav("/attendees");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F8F9FA" }}>
      <div
        className="h-48 sm:h-64 w-full bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1629197521865-4946b4acd2b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbmF2eSUyMHRleHR1cmUlMjBtaW5pbWFsJTIwbHV4dXJ5fGVufDB8fHx8MTc4MTEzOTI1MXww&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.85) 100%)" }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Welcome to</div>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl mt-2">Muslim Mingle</h1>
          <div className="mm-divider-gold mx-auto" style={{ background: "#D4AF37" }} />
          <p className="text-sm text-white/80 max-w-xs tracking-wide">Toronto Single Muslim Mixer</p>
        </div>
      </div>

      <div className="flex-1 max-w-md mx-auto w-full px-6 py-10 mm-fade-in">
        {step === "email" ? (
          <>
            <div className="overline mb-2">Step 1 of 2</div>
            <h2 className="font-serif-display text-3xl mb-1">Enter your email</h2>
            <p className="text-sm text-slate-600 mb-8">We'll send you a 6-digit code to log in. No password needed.</p>
            <form onSubmit={sendCode} className="space-y-8">
              <div>
                <label className="overline block mb-2" htmlFor="email">Email Address</label>
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email"
                  className="mm-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" data-testid="login-send-code-button" className="mm-btn-primary" disabled={loading}>
                {loading ? "Sending…" : "Send me a code"}
              </button>
            </form>
          </>
        ) : (
          <>
            <button onClick={() => { setStep("email"); setCode(""); }} className="mb-6 text-sm text-slate-600 flex items-center gap-1 hover:text-slate-900" data-testid="back-to-email">
              <ArrowLeft size={14} strokeWidth={1.5} /> Use a different email
            </button>
            <div className="overline mb-2">Step 2 of 2</div>
            <h2 className="font-serif-display text-3xl mb-1">Enter your code</h2>
            <p className="text-sm text-slate-600 mb-8">We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.</p>
            <form onSubmit={verify} className="space-y-8">
              <div>
                <label className="overline block mb-2" htmlFor="code">6-Digit Code</label>
                <input
                  id="code"
                  data-testid="login-code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  className="mm-input"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  style={{ letterSpacing: "0.3em", fontSize: "20px" }}
                  required
                />
              </div>
              <button type="submit" data-testid="login-verify-button" className="mm-btn-primary" disabled={loading}>
                {loading ? "Verifying…" : "Continue"}
              </button>
              <button type="button" onClick={sendCode} className="text-xs text-slate-500 hover:text-slate-700 underline w-full text-center" disabled={loading}>
                Didn't get it? Resend code
              </button>
            </form>
          </>
        )}

        <p className="text-xs text-slate-500 mt-10 leading-relaxed">
          Use the email you registered with on Eventbrite. Trouble logging in? Message the event organiser.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
